import { useMemo, useState } from "react";
import { z } from "zod";
import {
  Box,
  DENSITY_CONFIG,
  StatBand,
  WidgetEmpty,
  WidgetInteractive,
  compactNumber,
  type Omittable,
  type StatBandColumn,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { AGE_STATUS, AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { combineWhereConditions } from "#app/lib/query-utils";
import { DATE_PRESETS, datePresetKeySchema } from "#app/lib/utils/date-presets";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import type { WidgetDataSlice } from "#app/widgets/dashboards/widget-drilldown";
import {
  DRILLDOWN_ORDER_BY,
  sliceWhere,
} from "#app/widgets/dashboards/use-widget-drilldown-issues";
import { windowPhrase } from "#app/widgets/dashboards/scope/window-copy";
import type { IssueRow, SortDirection } from "#app/lib/data";
import { IssueTable, type IssueTableCountColumn } from "../shared/issue-table";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { comparisonBasis } from "../shared/comparison-window";
import { useStatStrip } from "./use-stat-strip";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";

/**
 * `stat-strip` — one row of headline numbers, each a drill target:
 * two now-state counts (Active, Needs attention) and two windowed flow counts
 * (Completed, Updates). Each tile is label, then figure, then caption — the
 * breakdown inside the count for the now-state pair, the same count over the
 * previous equal-length window for the flow pair — so the four tiles are one
 * shape and every caption is a FACT rather than arithmetic over one.
 * One widget rather than several `metric` cards because the managed grid snaps
 * Half/Full only. Every number is an existing server aggregation — see
 * `useStatStrip` for the two window semantics and the query fold.
 *
 * The four columns are the library's `StatBand` — the same band the outcome
 * summary draws, so two cards of headline figures on one page are one shape
 * rather than two. The widget owns what the figures MEAN (the labels, the
 * formatting, the drill slices); the band owns how a row of figures reads.
 *
 * Clicking a stat expands its rows INLINE — a bounded table below the strip
 * (the team-members expansion pattern), toggled per stat. For the three
 * project counts the table's `where` is the hook's now-state fold AND the
 * stat's exact drill slice — the same predicate the Data drawer folds — so its
 * total matches the number by construction. Updates counts EVENTS, so its rows
 * are the projects the Updates landed on, named by the aggregation itself (an
 * `issues` slice, exact on its own), with an Updates column beside the name that sums
 * to the figure. The card is `autoHeight`: expansion grows the card and the
 * grid reflows the rows below it.
 *
 * Loading is the band's own state: it keeps every label and press target and
 * stands placeholders for the figures, so the first paint and the re-query
 * after the window, the scope or the Filters change are one picture — nothing
 * above or below the numbers moves, and an open table stays open and reloads
 * in place. The count hooks hold the previous answer through that re-query, so
 * the Updates drill keeps the rows its figure named until the new figure names
 * others.
 */
export const statStripConfigSchema = z
  .looseObject({
    window: datePresetKeySchema.default("this-week"),
    showActive: z.boolean().default(true),
    showAttention: z.boolean().default(true),
    showClosed: z.boolean().default(true),
    showUpdates: z.boolean().default(true),
    compact: z.boolean().default(false),
    filters: widgetFiltersSchema.nullish(),
  });

export type StatStripConfig = z.infer<typeof statStripConfigSchema>;

/**
 * Data rows the inline table is sized for. A CONSTANT, deliberately not the
 * clicked stat's own count: the table opens at ONE height for every stat, so
 * clicking Active then Needs attention resizes nothing and the board below
 * never reflows. A stat with fewer rows leaves quiet space; a longer one
 * scrolls inside the reserve.
 *
 * Sizing off the stat's value is what made the card jump — Active's dozens and
 * Needs attention's two produced a ~290px swing on one click.
 */
export const STAT_RESERVE_ROWS = 8.5;

/** Row order for a drill whose rows are the projects a stat's events landed
 *  on: the project just posted on leads. A project count's rows keep the
 *  drawer's newest-created order. */
const UPDATED_ORDER_BY: Record<string, SortDirection> = { lastUpdateDate: "desc" };

const EMPTY_ROWS: IssueRow[] = [];

type StripStat = Omittable<{
  key: string;
  label: string;
  value: number;
  /** The caption under the figure. Empty only where the stat has nothing true
   *  left to add — an all-time window with no previous window, or a count of
   *  zero, which has no interior to break down. */
  sub: string;
  /** The exact drill slice for this stat's rows; null = not expandable. */
  slice: WidgetDataSlice | null;
  /** How many rows the drill lists — the figure itself for a project count,
   *  the number of projects for a stat that counts the events on them. The
   *  table paints a sized skeleton for this many under its header while the
   *  rows load. */
  rowCount: number;
  /** The expanded table's figure column, beside the name, for a stat that counts
   *  events: how many landed on each row, summing to the figure. */
  countColumn?: IssueTableCountColumn;
  /** Row order for the expanded table; the drawer's newest-created first
   *  unless the stat says otherwise. */
  orderBy?: Record<string, SortDirection>;
  /** Empty-state title for the expanded table — names the window where the
   *  stat is windowed, so an empty table explains itself. */
  emptyTitle: string;
}>;

/** A tier inside a headline count. A tier that IS the whole count is named
 *  rather than numbered: "19 / 19 lapsed" spends a line restating the
 *  number above it, which is what a caption is there to avoid. */
function subsetText(part: number, total: number, noun: string): string {
  if (total === 0) return "";
  if (part === total) return `all ${noun}`;
  if (part === 0) return `none ${noun}`;
  return `${part.toLocaleString()} ${noun}`;
}

/** A windowed stat's own count over the previous equal-length window. Stated
 *  as the raw count rather than the difference: "vs 7 last week" under a "3"
 *  gives the trend AND its scale, and it never degenerates into the "±0" a
 *  signed difference produces on the flat weeks that are most of them. */
function comparisonText(previous: number, basis: string): string {
  return `vs ${previous.toLocaleString()} ${basis}`;
}

/** Update needed is two tiers summed, so its caption shows the split when
 *  both are populated and falls back to naming the single populated one. The
 *  tier words are the age-status labels the chips below the strip wear, in
 *  running-text case, so the caption and the rows it drills into agree. */
function attentionText(atRisk: number, offTrack: number): string {
  const due = AGE_STATUS_CONFIG.atRisk.label.toLowerCase();
  const lapsed = AGE_STATUS_CONFIG.offTrack.label.toLowerCase();
  if (atRisk > 0 && offTrack > 0) {
    return `${atRisk.toLocaleString()} ${due} · ${offTrack.toLocaleString()} ${lapsed}`;
  }
  if (offTrack > 0) return `all ${lapsed}`;
  if (atRisk > 0) return `all ${due}`;
  return "";
}

function StatStripRender({ w, config, expanded }: WidgetRenderContext<StatStripConfig>) {
  const data = useStatStrip({
    window: config.window,
    showActive: config.showActive,
    showAttention: config.showAttention,
    showClosed: config.showClosed,
    showUpdates: config.showUpdates,
    filters: config.filters,
  });
  // Click-through: each stat toggles an inline table of exactly the rows it
  // counted (Updates counts events, so its rows are the projects they landed on).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const phrase = windowPhrase(data.windowKey);
  const basis = comparisonBasis(data.windowKey);
  const completedLabel = phrase ? `Completed ${phrase}` : "Completed";

  const stats: StripStat[] = [];
  if (config.showActive) {
    stats.push({
      key: "active",
      label: "Active",
      value: data.active,
      sub: subsetText(data.activeInProgress, data.active, "in progress"),
      slice:
        data.openIds.length > 0
          ? { kind: "dimension", dimension: "stage", ids: data.openIds, label: "Active" }
          : null,
      rowCount: data.active,
      emptyTitle: "No active Projects",
    });
  }
  if (config.showAttention) {
    stats.push({
      key: "attention",
      label: "Update needed",
      value: data.attention,
      sub: attentionText(data.attentionAtRisk, data.attentionOffTrack),
      slice: {
        kind: "dimension",
        dimension: "age",
        ids: [AGE_STATUS.atRisk, AGE_STATUS.offTrack],
        label: "Update needed",
      },
      rowCount: data.attention,
      emptyTitle: "No Projects need an update",
    });
  }
  if (config.showClosed) {
    stats.push({
      key: "closed",
      label: completedLabel,
      value: data.closed,
      sub: data.closedPrev !== null && basis ? comparisonText(data.closedPrev, basis) : "",
      slice:
        data.doneIds.length === 0
          ? null
          : data.windowKey === "all-time"
            ? { kind: "dimension", dimension: "stage", ids: data.doneIds, label: "Completed" }
            : {
                kind: "dateRange",
                dateField: "creationDate",
                startMs: Date.parse(data.windowRange.startDate),
                // The resolved window's endDate is inclusive (…23:59:59.999);
                // the slice contract is [startMs, endMs) exclusive.
                endMs: Date.parse(data.windowRange.endDate) + 1,
                dimension: "stage",
                ids: data.doneIds,
                label: completedLabel,
              },
      rowCount: data.closed,
      emptyTitle: phrase ? `No Projects completed ${phrase}` : "No completed Projects",
    });
  }
  if (config.showUpdates) {
    const updatesLabel = phrase ? `Updates ${phrase}` : "Updates";
    stats.push({
      key: "updates",
      label: updatesLabel,
      value: data.updates,
      sub: data.updatesPrev !== null && basis ? comparisonText(data.updatesPrev, basis) : "",
      // The aggregation named the projects; its per-project figures are the
      // column beside them. Expandable at zero too — a zero opens the same
      // empty state the other stats open, never a dead tile among live ones.
      slice: { kind: "issues", ids: Object.keys(data.updateCounts), label: updatesLabel },
      // The rows are the projects the Updates landed on, not the Updates.
      rowCount: Object.keys(data.updateCounts).length,
      countColumn: { header: "Updates", counts: data.updateCounts },
      orderBy: UPDATED_ORDER_BY,
      emptyTitle: phrase ? `No Updates posted ${phrase}` : "No Updates posted",
    });
  }

  const expandedStat = stats.find((stat) => stat.key === expandedKey && stat.slice != null) ?? null;
  const expandedSlice = expandedStat?.slice ?? null;
  const orderBy = expandedStat?.orderBy ?? DRILLDOWN_ORDER_BY;
  const rowCount = expandedStat?.rowCount ?? 0;
  // A slice object is a fresh ref every render, so the query fold keys on its
  // content — the same guard `useWidgetDrilldownWhere` documents.
  const sliceKey = expandedSlice ? JSON.stringify(expandedSlice) : "";
  // Null while a stat is expanded means its set is empty and has no server
  // predicate (see `sliceWhere`): the table then shows the empty state from an
  // empty local set instead of paging.
  const tableQuery = useMemo(() => {
    if (!expandedSlice) return null;
    const narrowed = sliceWhere(expandedSlice);
    // An `issues` slice is exact by itself. The aggregation that named the
    // rows already folded the scope, and it narrowed by ACTOR where the
    // now-state fold narrows by assignee: AND the two and a project the viewer
    // posted on but is not assigned to counts above and vanishes below.
    if (expandedSlice.kind === "issues") {
      return narrowed ? { where: narrowed, orderBy, total: rowCount } : null;
    }
    const where = narrowed
      ? (combineWhereConditions([data.nowWhere, narrowed]) ?? data.nowWhere)
      : data.nowWhere;
    return { where, orderBy, total: rowCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sliceKey serializes exactly the fields sliceWhere reads
  }, [data.nowWhere, sliceKey, orderBy, rowCount]);

  if (data.error) throw data.error;
  if (stats.length === 0) {
    return <WidgetEmpty title="No stats selected" />;
  }

  // Internal scroll needs a bounded viewport: column-header + group-header
  // bands, then the constant row reserve, the half row being the peek that
  // says longer lists scroll. A floor, not a cap — the table grows into
  // whatever the card's whole-row span left over, so the peek only ever shows
  // MORE rows; that leftover is constant too, because the body it rounds up
  // from is.
  const density = DENSITY_CONFIG.regular;
  const tableHeight =
    2 * density.headerHeight + STAT_RESERVE_ROWS * density.rowHeight;

  // The card's row span rounds the body UP to whole rows, and the card hands
  // the leftover to the body rather than parking it as dead band. The TABLE is
  // what claims it — the band never does, because its own floor already fills
  // the rows the card claims: the numbers sit where they sat before the click,
  // and
  // the few px the rounding leaves land as quiet air UNDER the band instead
  // of re-splitting themselves above and below the figures on every growth.
  return (
    <Box
      direction="column"
      grow
      shrink={false}
      justify={expanded ? "center" : undefined}
    >
      <StatBand
        size={expanded ? "lg" : "md"}
        loading={data.loading}
        columns={stats.map<StatBandColumn>((stat) => ({
          id: stat.key,
          label: stat.label,
          value: config.compact ? compactNumber(stat.value) : stat.value.toLocaleString(),
          caption: stat.sub,
          selected: expandedStat?.key === stat.key,
          // A stat with no slice (no stage of its kind exists) has no rows to
          // disclose — the band renders it as text, and a press there grabs the
          // card like the rest of the body.
          onSelect: stat.slice
            ? () => setExpandedKey((current) => (current === stat.key ? null : stat.key))
            : undefined,
        }))}
      />
      {expandedStat ? (
        // An interactive island too: table rows open and edit — a press inside
        // must never grab the bodyDraggable card.
        <WidgetInteractive
          direction="column"
          marginTop="sm"
          // The card's autoHeight body takes an `xs` bottom inset — enough for
          // a body that ends in a line of text, too tight for a bordered
          // island, whose gutter then reads uneven against the card's `md`
          // sides. Bring the gap up to the side inset so the table sits evenly
          // on all three edges. Only the grid card needs it: the expanded
          // dialog pads its preview symmetrically already.
          marginBottom={expanded ? undefined : "base"}
          grow
          shrink={false}
          rounded="md"
          border
          overflow="hidden"
          surface="card"
        >
          <Box
            direction="column"
            grow
            shrink={false}
            // The reserve holds for an empty stat too. Letting WidgetEmpty take
            // its natural height instead reopens the jump from the other side:
            // clicking a populated stat and then a zero one would collapse the
            // card exactly as a per-stat height did.
            height={tableHeight}
          >
            <IssueTable
              query={tableQuery ?? undefined}
              rows={tableQuery ? undefined : EMPTY_ROWS}
              // A stat with no predicate yet — the Updates drill before its
              // first answer names any project — is loading, not empty.
              loading={data.loading}
              w={w}
              emptyTitle={expandedStat.emptyTitle}
              groupLabel={expandedStat.label}
              countColumn={expandedStat.countColumn}
              // The rows disclose the figure above them; they are not a set to
              // act on, so no checkbox column and no bulk bar.
              selectable={false}
            />
          </Box>
        </WidgetInteractive>
      ) : null}
    </Box>
  );
}

const statStripSettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: [
      {
        key: "window",
        kind: "select",
        label: "Window",
        icon: "calendar",
        // Completed/Updates count within this window; Active and Needs attention
        // are now-state and ignore it. Custom needs a range picker the
        // settings drawer doesn't offer, so it is not listed (a custom range
        // still reaches the strip through the Filters date range).
        options: DATE_PRESETS.filter((preset) => preset.key !== "custom").map((preset) => ({
          value: preset.key,
          label: preset.label,
        })),
      },
      { key: "showActive", kind: "toggle", label: "Active Projects", icon: "hash" },
      { key: "showAttention", kind: "toggle", label: "Update needed", icon: "alert-triangle" },
      { key: "showClosed", kind: "toggle", label: "Completed in window", icon: "check-circle" },
      { key: "showUpdates", kind: "toggle", label: "Updates in window", icon: "activity" },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "compact", kind: "toggle", label: "Abbreviate large numbers", icon: "minimize" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const statStripDef: WidgetModule<StatStripConfig> = {
  type: "stat-strip",
  title: "Projects at a glance",
  icon: "trending-up",
  // autoHeight: the collapsed strip is the band plus the card's chrome — two
  // rows — and an expanded stat's inline table grows the card so the grid
  // reflows below it. minH is that floor, and maxH bounds the derived rows, so
  // it must clear band + bounded table.
  span: { minW: 6, minH: 2, maxW: 12, maxH: 16, defaultW: 12 },
  autoHeight: true,
  configSchema: statStripConfigSchema,
  settings: statStripSettings,
  headerActions: WidgetHeaderFilters,
  // A row of numbers: grab the card from anywhere; each stat and the expanded
  // table are carved out as interactive islands (drag activation needs 4px of
  // movement). Headed rather than headerless even though the tile labels name
  // every figure — a headerless tile floats its action cluster over the body,
  // and the body here is four full-width tiles with a label in the corner the
  // cluster lands on.
  bodyDraggable: true,
  render: StatStripRender,
  catalog: [
    {
      key: "stat-strip.default",
      label: "Projects at a glance",
      description:
        "Active Projects, what needs an update, and what was completed in the window — one row of numbers.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects", "ageHealth", "activity"] satisfies readonly WidgetTag[],
      // Every field carries its own schema default, so the empty blob is the
      // full designed strip.
      defaultConfig: {},
      defaultW: 12,
    },
  ],
};